/**
 * Singleton audio instances + the active settings object. Scenes import
 * `audio.music`, `audio.sfx`, `audio.fileSfx` and `audio.settings` rather
 * than constructing their own.
 */

import { Music } from './music';
import { Sfx } from './sfx';
import { FileSfx } from './fileSfx';
import { loadSettings, saveSettings, type AudioSettings } from './settings';

const settings: AudioSettings = loadSettings();
const music = new Music();
const sfx = new Sfx();
const fileSfx = new FileSfx();

// Apply loaded settings to the instances.
music.setVolume(settings.musicVolume);
sfx.setVolume(settings.sfxVolume);
fileSfx.setVolume(settings.sfxVolume);
music.setMuted(settings.muted);
sfx.setMuted(settings.muted);
fileSfx.setMuted(settings.muted);

// Browsers require a user gesture to start/resume AudioContexts. Register a
// one-shot listener on the first click/keydown/touchstart that resumes all
// three contexts. Sources already scheduled (e.g. music) will begin playing
// immediately once their context transitions to "running".
function resumeAllContexts(): void {
  for (const ev of ['click', 'keydown', 'touchstart'] as const) {
    document.removeEventListener(ev, resumeAllContexts, true);
  }
  sfx.resume();
  fileSfx.resume();
  music.resume();
}
for (const ev of ['click', 'keydown', 'touchstart'] as const) {
  document.addEventListener(ev, resumeAllContexts, { capture: true, once: false });
}

export const audio = {
  music,
  sfx,
  fileSfx,
  settings,

  setMusicVolume(v: number): void {
    settings.musicVolume = Math.max(0, Math.min(1, v));
    music.setVolume(settings.musicVolume);
    saveSettings(settings);
  },

  setSfxVolume(v: number): void {
    settings.sfxVolume = Math.max(0, Math.min(1, v));
    sfx.setVolume(settings.sfxVolume);
    fileSfx.setVolume(settings.sfxVolume);
    saveSettings(settings);
  },

  setMuted(m: boolean): void {
    settings.muted = m;
    music.setMuted(m);
    sfx.setMuted(m);
    fileSfx.setMuted(m);
    saveSettings(settings);
    if (!m) {
      sfx.resume();
      fileSfx.resume();
      music.resume();
    }
  },

  toggleMuted(): boolean {
    audio.setMuted(!settings.muted);
    return settings.muted;
  },
};
